import React, { useState } from 'react';
import axios from 'axios';
import { useWebsiteContent } from '../WebsiteContentContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Contact = () => {
  const cms = useWebsiteContent();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    subject: 'General inquiry',
    message: '',
  });
  const [status, setStatus] = useState({ type: 'idle', text: '' });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: 'error', text: 'Please fill in your name, email, and message.' });
      return;
    }
    setStatus({ type: 'loading', text: 'Sending…' });
    try {
      await axios.post(`${API}/contact-messages`, form, { timeout: 15000 });
      setStatus({ type: 'success', text: "Thanks — we've received your message and will reply within one business day." });
      setForm({ name: '', email: '', phone: '', organization: '', subject: 'General inquiry', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not send the message. Please try again or email us directly.';
      setStatus({ type: 'error', text: detail });
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: copy + contact info */}
            <div>
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-blue-600">{cms.contact_eyebrow}</span>
              <h1 className="mt-4 text-5xl sm:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">{cms.contact_title}</h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-lg">{cms.contact_subtitle}</p>

              <div className="mt-12 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Email</div>
                    <a href={`mailto:${cms.contact_email}`} className="text-slate-900 font-semibold hover:text-blue-600 transition" data-testid="contact-email">{cms.contact_email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Support hours</div>
                    <div className="text-slate-900 font-semibold">{cms.contact_hours}</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Based in</div>
                    <div className="text-slate-900 font-semibold">{cms.contact_location}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: form */}
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-blue-100/40 p-8 lg:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Your name *" value={form.name} onChange={update('name')} testid="contact-input-name" />
                <Field label="Email *" type="email" value={form.email} onChange={update('email')} testid="contact-input-email" />
                <Field label="Phone" value={form.phone} onChange={update('phone')} testid="contact-input-phone" />
                <Field label="Organization" value={form.organization} onChange={update('organization')} testid="contact-input-organization" />
              </div>

              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject</label>
                <select
                  data-testid="contact-input-subject"
                  value={form.subject}
                  onChange={update('subject')}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                >
                  <option>General inquiry</option>
                  <option>Sales / Demo request</option>
                  <option>Technical support</option>
                  <option>Partnership</option>
                  <option>Other</option>
                </select>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Message *</label>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update('message')}
                  rows={5}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                  placeholder="Tell us a bit about your team and what you're hoping to improve..."
                />
              </div>

              {status.type !== 'idle' && (
                <div
                  data-testid="contact-form-status"
                  className={`mt-5 px-4 py-3 rounded-xl text-sm ${
                    status.type === 'success'
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : status.type === 'error'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-blue-50 text-blue-800 border border-blue-200'
                  }`}
                >
                  {status.text}
                </div>
              )}

              <button
                type="submit"
                disabled={status.type === 'loading'}
                data-testid="contact-submit-button"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold shadow-lg shadow-blue-600/30 transition"
              >
                {status.type === 'loading' ? 'Sending…' : 'Send message'}
                {status.type !== 'loading' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
    />
  </div>
);

export default Contact;
